import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Badge, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';

import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import useStickyState from 'hooks/useStickyState';
import useDraftManuals from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManuals';
import useVerticals from 'data_sources/useVerticals';
import useManualTypes from 'data_sources/useManualTypes';
import useRelatedOrganizations from 'views/App/Portal/Dashboard/RiskPoolDashboard/_data_sources/useRelatedOrganizations';
import useJurisdictions from 'data_sources/useJurisdictions';
import errorHelper from 'helpers/errorHelper';
import { DraftManualsContext } from '../context';
import { AppContext } from 'views/App/context';
import LexViewToggle from '_lex/_atoms/LexViewToggle';
import type { ViewMode } from '_lex/_atoms/LexViewToggle';
import { DraftManualItem, ManualPermissions } from 'views/App/Portal/Policy/_types/manuals/draft';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import DraftManualsPolicyPageToolbar from './_components/PolicyPageToolbar';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import ExportModal from 'views/App/Portal/Policy/DraftManuals/Detail/ExportModal';
import DraftManualsListView from './_components/DraftManualsListView';
import DraftManualsGridView from './_components/DraftManualsGridView';

type DraftManualsUrlParams = {
  search?: string | null;
  verticalId?: string | null;
  sortOrder?: 'TITLE_ASCENDING' | 'TITLE_DESCENDING';
  orgId?: string | null;
  jurisdictionId?: string | null;
};

const defaultParams: DraftManualsUrlParams = {
  search: null,
  verticalId: null,
  sortOrder: 'TITLE_ASCENDING',
  orgId: null,
  jurisdictionId: null,
};

const DraftManualsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { manualHasPermissions, setManualPermissions } = useContext(DraftManualsContext);
  const {
    currentProfile,
    currentOrganizationId,
    isLexipolStaff,
    hasPermission,
    userOrganizationId,
  } = useContext(AppContext);

  const [params, setParams] = useUrlParams(defaultParams, { storage: true });
  const [sortModel, setSortModel] = React.useState([
    { field: 'name', sort: params.sortOrder === 'TITLE_DESCENDING' ? 'desc' : 'asc' },
  ]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedManualForExport, setSelectedManualForExport] = useState<DraftManualItem | null>(null);
  const [viewMode, setViewMode] = useStickyState<ViewMode>('phx-draftManuals-viewMode', 'grid');

  useEffect(() => {
    setSortModel([{ field: 'name', sort: params.sortOrder === 'TITLE_DESCENDING' ? 'desc' : 'asc' }]);
  }, [params.sortOrder]);

  const updateParams = useCallback(
    (updatedParams: DraftManualsUrlParams) => {
      setParams((prev: any) => ({ ...prev, ...updatedParams }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams],
  );

  const queryParams = useMemo(() => {
    const newParams: any = { ...params };
    Object.keys(newParams).forEach((key) => {
      if (newParams[key] === null || newParams[key] === '') {
        delete newParams[key];
      }
    });
    if (currentProfile?.user?.orgId && currentOrganizationId !== currentProfile?.user?.orgId) {
      newParams.orgId = currentOrganizationId;
    }
    return newParams;
  }, [params, currentProfile?.user?.orgId, currentOrganizationId]);

  const manualsQuery = useDraftManuals(queryParams, {
    onSuccess: (data: any) => {
      updateManualPermissions(data);
    },
  });

  const isRiskPoolUser = currentProfile?.organization?.riskPoolEnabled;

  useEffect(() => {
    if (isLexipolStaff || isRiskPoolUser) {
      setViewMode('list');
    }
  }, [isLexipolStaff, isRiskPoolUser]);

  const { data: relatedOrgsData } = useRelatedOrganizations(
    currentOrganizationId || '',
    { enabled: !!isRiskPoolUser && !!currentOrganizationId },
    (errors) => errorHelper.enqueueErrors(errors),
  );

  const { data: jurisdictions } = useJurisdictions({ enabled: !!isLexipolStaff }, (errors) =>
    errorHelper.enqueueErrors(errors),
  );

  const organizationOptions = useMemo(() => {
    if (!relatedOrgsData?.content) return [];
    return relatedOrgsData.content
      .map((org) => ({ value: org.id, label: org.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [relatedOrgsData]);

  const jurisdictionOptions = useMemo(() => {
    if (!jurisdictions) return [];
    return jurisdictions
      .map((jurisdiction) => ({ value: jurisdiction.id, label: jurisdiction.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [jurisdictions]);

  const updateManualPermissions = useCallback(
    (data: any) => {
      const mPerms: ManualPermissions = {};
      if (data.pages?.length > 0 && data.pages[0].content) {
        (data.pages ? data.pages.flatMap((x: any) => x.content) : ([] as DraftManualItem[])).forEach(
          (m: DraftManualItem) => {
            mPerms[m.id] = (m.authorizations ?? []).map((x) => x.action).sort();
          },
        );
        setManualPermissions(mPerms);
      }
    },
    [setManualPermissions],
  );

  useEffect(() => {
    if (manualsQuery.data) {
      updateManualPermissions(manualsQuery.data);
    }
  }, [manualsQuery.data, updateManualPermissions]);

  const manuals = useMemo(
    () => (manualsQuery.data?.pages ? manualsQuery.data.pages.flatMap((x) => x.content) : []),
    [manualsQuery.data],
  );

  const verticalsQuery = useVerticals();
  const manualTypesQuery = useManualTypes();
  const { createAgencyManual } = useManageManual();

  const handleExportClick = useCallback((manual: DraftManualItem) => {
    setSelectedManualForExport(manual);
    setExportModalOpen(true);
  }, []);

  const handlePublish = useCallback(
    (manual: DraftManualItem) => {
      navigate(`./${manual.id}/publish-approved`);
    },
    [navigate],
  );

  const handleManualClick = useCallback(
    (manual: DraftManualItem) => {
      navigate(`./${manual.id}`);
    },
    [navigate],
  );

  const buildActionMenuItems = useCallback(
    (manual: DraftManualItem): ActionMenuItemProps[] => {
      const actions: ActionMenuItemProps[] = [];
      const isGlobalManual = manual.name.toLowerCase().includes('global');

      if (manualHasPermissions(manual.id, ['issuePolicy'])) {
        actions.push({
          key: `${manual.id}_ack_settings`,
          label: t('draftManuals.actions.acknowledgementSettings'),
          href: `./${manual.id}/acknowledgement-settings`,
        });
      }
      if (manualHasPermissions(manual.id, ['listGIQQuestions'])) {
        actions.push({
          key: `${manual.id}_giq`,
          label: t('draftManuals.actions.giq'),
          href: `./${manual.id}/giq`,
        });
      }
      if (manualHasPermissions(manual.id, ['listCRQQuestions'])) {
        actions.push({
          key: `${manual.id}_crq`,
          label: t('draftManuals.actions.crq'),
          href: `./${manual.id}/crq`,
        });
      }
      // TODO: add correct permissions for these action
      if (manualHasPermissions(manual.id, ['listCRQQuestions'])) {
        actions.push({
          key: `${manual.id}_properties`,
          label: t('draftManuals.actions.properties'),
          href: `./${manual.id}/manual-properties`,
        });
      }
      if (isGlobalManual && isLexipolStaff && hasPermission('createLexipolManual', currentOrganizationId)) {
        actions.push({
          key: `${manual.id}_create_jurisdiction`,
          label: t('draftManuals.actions.createJurisdictionManual'),
          href: `./create-jurisdiction-manual/${manual.id}`,
        });
      }

      return actions;
    },
    [manualHasPermissions, isLexipolStaff, hasPermission, currentOrganizationId],
  );

  const getVerticalName = useCallback(
    (verticalId: string) => {
      if (verticalsQuery.data) {
        return verticalsQuery.data.find((v) => v.id === verticalId)?.name;
      }
    },
    [verticalsQuery.data],
  );

  const handleSortModelChange = (model: any) => {
    setSortModel(model);
    if (model.length > 0) {
      const { field, sort } = model[0];
      if (field === 'name') {
        updateParams({ sortOrder: sort === 'desc' ? 'TITLE_DESCENDING' : 'TITLE_ASCENDING' });
      }
    }
  };

  const isFiltered = useMemo(
    () => params.search || params.verticalId || params.orgId || params.jurisdictionId,
    [params],
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const handleCreateGlobalManual = () => {
    navigate('./create-global-manual');
  };

  const handleCreateAgencySupplementalManual = async () => {
    if (!currentOrganizationId) {
      enqueueSnackbar(t('errors.organizationNotFound'), { variant: 'error' });
      return;
    }

    const payload = { name: 'Supplemental Manual', targetOrgId: currentOrganizationId };

    createAgencyManual(
      userOrganizationId || '',
      payload,
      () => {
        enqueueSnackbar(
          t('draftManuals.createAgencySupplementalManual.success', '', { manualName: 'Supplemental Manual' }),
          { variant: 'success' },
        );
        queryClient.invalidateQueries(['draft_manuals']);
      },
      (error) => {
        enqueueSnackbar(error.message || t('errors.createManualFailed'), { variant: 'error' });
      },
    );
  };

  const handleCreateAgencyManual = () => {
    navigate('/admin/policy/draft-manuals/create-agency-manual');
  };

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: manualsQuery.hasNextPage,
      isFetchingNextPage: manualsQuery.isFetchingNextPage,
      fetchNextPage: manualsQuery.fetchNextPage,
      isError: manualsQuery.isError,
      isLoading: manualsQuery.isLoading,
      error: manualsQuery.error,
      data: {
        pages:
          manualsQuery.data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [manualsQuery],
  );

  const totalCount = manualsQuery.data?.pages[0]?.count ?? 0;

  return (
    <CommonLayout
      headerProps={{
        title: t('policy.draftManuals.title'),
        subtitle: isRiskPoolUser ? t('policy.draftManuals.subtitleRiskPool') : t('policy.draftManuals.subtitle'),
        titleBadge:
          totalCount > 0 ? (
            <Badge
              variant='inline'
              badgeContent={totalCount}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', gap: 4, minHeight: 0 }}>
        <DraftManualsPolicyPageToolbar
          params={params}
          updateParams={updateParams}
          isFiltered={isFiltered}
          clearParams={clearParams}
          verticalsData={verticalsQuery.data}
          jurisdictionOptions={jurisdictionOptions}
          organizationOptions={organizationOptions}
          onCreateGlobalManual={handleCreateGlobalManual}
          onCreateAgencySupplementalManual={handleCreateAgencySupplementalManual}
          onCreateAgencyManual={handleCreateAgencyManual}
        />
        {!isLexipolStaff && !isRiskPoolUser && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            <LexViewToggle value={viewMode} onChange={setViewMode} />
          </Box>
        )}

        {isLexipolStaff || isRiskPoolUser || viewMode === 'list' ? (
          <DraftManualsListView
            manuals={manuals as unknown as DraftManualItem[]}
            query={gridViewQuery}
            totalCount={totalCount}
            sorting={{ sortModel, onSortModelChange: handleSortModelChange }}
            onExport={handleExportClick}
            onPublish={handlePublish}
            getVerticalName={getVerticalName}
            manualHasPermissions={manualHasPermissions}
            buildActionMenuItems={buildActionMenuItems}
            isRiskPoolUser={!!isRiskPoolUser}
          />
        ) : (
          <DraftManualsGridView
            manuals={manuals as unknown as DraftManualItem[]}
            hasNextPage={manualsQuery.hasNextPage}
            fetchNextPage={manualsQuery.fetchNextPage}
            isFetchingNextPage={manualsQuery.isFetchingNextPage}
            isLoading={manualsQuery.isLoading}
            getVerticalName={getVerticalName}
            manualHasPermissions={manualHasPermissions}
            buildActionMenuItems={buildActionMenuItems}
            onPublish={handlePublish}
            onExport={handleExportClick}
            onManualClick={handleManualClick}
          />
        )}
      </Box>
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        allowTypeSelection
        externalManual={selectedManualForExport}
      />
    </CommonLayout>
  );
};

export default DraftManualsList;
